import { useCallback, useEffect, useState } from "react";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminEmptyState,
  AdminErrorBanner,
  AdminPageLayout,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextInput,
  AdminTr,
} from "@/components/admin";
import { fetchAdminActionAuditLogs, type AdminActionAuditLogItem } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const reasonLabels: Record<string, string> = {
  OPERATOR_REQUEST: "운영 요청",
  DATA_CORRECTION: "데이터 교정",
  SECURITY_RESPONSE: "보안 대응",
  TEST_ROLLBACK: "테스트·원복",
  OTHER: "기타",
};

const AuditLogsPage = () => {
  const [items, setItems] = useState<AdminActionAuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [actionCode, setActionCode] = useState("");
  const [targetType, setTargetType] = useState("");
  const [targetId, setTargetId] = useState("");
  const [requestId, setRequestId] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [resultCode, setResultCode] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadLogs = useCallback(async (requestedPage = page) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await fetchAdminActionAuditLogs({
        actionCode: actionCode.trim() || undefined,
        targetType: targetType.trim() || undefined,
        targetId: targetId.trim() || undefined,
        requestId: requestId.trim() || undefined,
        reasonCode: reasonCode || undefined,
        resultCode: resultCode || undefined,
        page: requestedPage,
        size: 50,
      });
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
      setTotalPages(result.totalPages);
    } catch (error) {
      setItems([]);
      setTotal(0);
      setErrorMessage(error instanceof Error ? error.message : "감사로그를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [actionCode, page, reasonCode, requestId, resultCode, targetId, targetType]);

  useEffect(() => {
    void loadLogs();
    // 최초 진입 시 전체 최신 로그를 조회한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminPageLayout
      title="관리자 감사로그"
      subtitle="관리자 상태 변경·삭제·해제 작업의 실행자, 사유와 결과 추적"
      actions={<AdminButton variant="soft" size="sm" icon="ri-refresh-line" disabled={loading} onClick={() => void loadLogs()}>{loading ? "조회 중..." : "새로고침"}</AdminButton>}
    >
      <AdminErrorBanner message={errorMessage} />
      <AdminCard className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <AdminTextInput value={actionCode} onChange={(event) => setActionCode(event.target.value)} placeholder="동작 코드" />
          <AdminTextInput value={targetType} onChange={(event) => setTargetType(event.target.value)} placeholder="대상 유형" />
          <AdminTextInput value={targetId} onChange={(event) => setTargetId(event.target.value)} placeholder="대상 ID" />
          <AdminTextInput value={requestId} onChange={(event) => setRequestId(event.target.value)} placeholder="Request ID" />
          <AdminSelect value={reasonCode} onValueChange={setReasonCode}><option value="">전체 사유 유형</option>{Object.entries(reasonLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</AdminSelect>
          <AdminSelect value={resultCode} onValueChange={setResultCode}><option value="">전체 결과</option><option value="SUCCESS">성공</option><option value="REJECTED">거부</option><option value="FAILED">실패</option></AdminSelect>
          <AdminButton variant="primary" size="sm" icon="ri-search-line" disabled={loading} onClick={() => { setPage(0); void loadLogs(0); }}>조회</AdminButton>
          <AdminButton variant="secondary" size="sm" icon="ri-restart-line" disabled={loading} onClick={() => { setActionCode(""); setTargetType(""); setTargetId(""); setRequestId(""); setReasonCode(""); setResultCode(""); setPage(0); }}>필터 초기화</AdminButton>
        </div>
      </AdminCard>

      <AdminCard className="overflow-hidden">
        <AdminCardHeader icon="ri-history-line" title={`감사 이력 · 전체 ${total}건 / 최근 ${items.length}건`} />
        {items.length === 0 && !loading ? (
          <AdminEmptyState title="조건에 맞는 감사로그가 없습니다." description="필터를 비우고 다시 조회해 주세요." />
        ) : (
          <AdminTable headers={["시각", "관리자", "동작", "대상", "변경", "사유", "결과·요청 ID"]}>
            {items.map((item) => (
              <AdminTr key={item.auditLogId}>
                <AdminTd><div className="whitespace-nowrap text-xs">{formatDateTime(item.performedAt)}</div><div className="text-xs text-slate-400">#{item.auditLogId}</div></AdminTd>
                <AdminTd><div className="font-medium text-slate-700">{item.actorName ?? item.actorLoginId ?? `관리자 #${item.actorAdminLoginAccountId}`}</div><div className="text-xs text-slate-400">{item.actorRoleCode}</div></AdminTd>
                <AdminTd><code className="text-xs text-blue-700">{item.actionCode}</code></AdminTd>
                <AdminTd><div className="text-xs">{item.targetType}</div><div className="text-xs text-slate-400">ID {item.targetId}</div></AdminTd>
                <AdminTd><div className="max-w-48 break-all text-xs text-slate-500">{item.beforeSummary ?? "-"}</div><div className="my-0.5 text-xs text-slate-300">↓</div><div className="max-w-48 break-all text-xs text-slate-700">{item.afterSummary ?? "-"}</div></AdminTd>
                <AdminTd><div className="text-xs font-medium text-slate-600">{reasonLabels[item.reasonCode] ?? item.reasonCode}</div><div className="mt-1 max-w-56 break-words text-xs text-slate-700">{item.reason}</div></AdminTd>
                <AdminTd><AdminStatusBadge label={item.resultCode} tone={item.resultCode === "SUCCESS" ? "success" : "danger"} /><div className="mt-1 max-w-44 break-all text-xs text-slate-400">{item.requestId}</div></AdminTd>
              </AdminTr>
            ))}
          </AdminTable>
        )}
        {totalPages > 1 && <div className="flex items-center justify-center gap-3 border-t border-slate-100 p-3"><AdminButton variant="secondary" size="sm" disabled={loading || page === 0} onClick={() => void loadLogs(page - 1)}>이전</AdminButton><span className="text-xs text-slate-500">{page + 1} / {totalPages}</span><AdminButton variant="secondary" size="sm" disabled={loading || page + 1 >= totalPages} onClick={() => void loadLogs(page + 1)}>다음</AdminButton></div>}
      </AdminCard>
    </AdminPageLayout>
  );
};

export default AuditLogsPage;
